import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../configuration.scss';
import './roleoAutomatico.scss';
import RoleoAutomaticoModal from './RoleoAutomaticoModal';
import { getAllCourts } from '../../../../services/courtServices';
import {
  getTribunalesUsuarioPaged,
  updateTribunalesUsuarios,
  updateTribunalesUsuariosExcel,
  logRoleoAutomaticoDownload,
} from '../../../../services/roleoAutomaticoService';
import { getRegiones } from '../../../../services/roleoAutomaticoService';
import { getCurrentUser } from '../../../../services/authService';
import {
  getAllBusinessAreas,
  updateUserBusinessArea,
} from '../../../../services/usersServices';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { roleoAutomaticoI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';

const normalizeTypeSearch = (type) => {
  if (!type) return null;
  const lower = String(type).toLowerCase();
  if (lower === 'case' || lower.includes('caso')) return 'case';
  if (lower === 'court' || lower.includes('tribunal')) return 'court';
  if (lower === 'area' || lower.includes('área') || lower.includes('area')) {
    return 'area';
  }
  return null;
};

const getFilterColor = (type) => {
  const normalized = normalizeTypeSearch(type);
  if (normalized === 'case') return 'info';
  if (normalized === 'court') return 'warning';
  if (normalized === 'area') return 'primary';
  return 'light';
};

const mapSortKeyToBackend = (key) => {
  switch (key) {
    case 'user':
      return 'userName';
    case 'email':
      return 'email';
    case 'filterType':
      return 'typeSearch';
    case 'courts':
      return 'tribunalList';
    default:
      return null;
  }
};

const parseTribunalList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeTribunalItems = (value) => {
  const items = parseTribunalList(value);
  return items
    .map((item) => {
      if (typeof item === 'number') return { id: item, name: '' };
      if (typeof item === 'string') return { id: null, name: item };
      return {
        id:
          item.id ??
          item.Id ??
          item.value ??
          item.Value ??
          item.codigo ??
          item.codigoTribunal ??
          null,
        name:
          item.nombre ??
          item.Nombre ??
          item.name ??
          item.label ??
          item.nombreTribunal ??
          '',
      };
    })
    .filter((item) => item.id != null || item.name);
};

export default function RoleoAutomatico() {
  const { language } = useLanguage();
  const roleoLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: roleoAutomaticoI18n });
  const { showSnackbar } = useSnackbar();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const {
    Card,
    Input,
    Dropdown,
    Table,
    IconButton,
    Badge,
    Button,
    Modal,
    ComboBox,
    SvgIcon,
  } = DesignSystemComponents ?? {};

  const filterOptions = useMemo(
    () => [
      { label: t('roleo_auto_filter_all'), value: 'all' },
      { label: t('roleo_auto_filter_case'), value: 'caso' },
      { label: t('roleo_auto_filter_court'), value: 'tribunal' },
      { label: t('roleo_auto_filter_area'), value: 'area' },
    ],
    [t]
  );

  const areaAllOption = useMemo(
    () => ({ label: t('roleo_auto_filter_all'), value: 'all' }),
    [t]
  );

  const exportHeaders = useMemo(
    () => [
      t('roleo_auto_export_header_user'),
      t('roleo_auto_export_header_email'),
      t('roleo_auto_export_header_filter'),
      t('roleo_auto_export_header_courts'),
    ],
    [t]
  );

  const templateHeaders = useMemo(
    () => [
      t('roleo_auto_template_header_user'),
      t('roleo_auto_template_header_email'),
      t('roleo_auto_template_header_filter'),
      t('roleo_auto_template_header_courts'),
      t('roleo_auto_template_header_business_area_id'),
      t('roleo_auto_template_header_business_area'),
    ],
    [t]
  );

  const templateExample = useMemo(
    () => [
      t('roleo_auto_template_example_user'),
      t('roleo_auto_template_example_email'),
      t('roleo_auto_template_example_filter'),
      t('roleo_auto_template_example_courts'),
      t('roleo_auto_template_example_business_area_id'),
      t('roleo_auto_template_example_business_area'),
    ],
    [t]
  );

  const [tribunals, setTribunals] = useState([]);
  const [tribunalsLoading, setTribunalsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [areaValue, setAreaValue] = useState('all');
  const [areasLoading, setAreasLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [filterValue, setFilterValue] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableKey, setTableKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchDebounced, setSearchDebounced] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsMenuRef = useRef(null);
  const importInputRef = useRef(null);

  const { canExportImport, canEditAutoRotation } =
    usePermissionsConfiguration();

  useEffect(() => {
    roleoAutomaticoI18n.changeLanguage(roleoLanguage);
  }, [roleoLanguage]);

  useEffect(() => {
    setAreaOptions((prev) => {
      const withoutAll = prev.filter((option) => option.value !== 'all');
      return [areaAllOption, ...withoutAll];
    });
  }, [areaAllOption]);

  const tribunalsById = useMemo(() => {
    return tribunals.reduce((acc, tribunal) => {
      acc[tribunal.id] = tribunal.name;
      return acc;
    }, {});
  }, [tribunals]);

  const businessAreaById = useMemo(() => {
    return areaOptions.reduce((acc, area) => {
      if (area.value !== null && area.value !== undefined) {
        acc[area.value] = area.label;
      }
      return acc;
    }, {});
  }, [areaOptions]);

  const getFilterLabel = (type) => {
    const normalized = normalizeTypeSearch(type);
    if (normalized === 'case') return t('roleo_auto_filter_case');
    if (normalized === 'court') return t('roleo_auto_filter_court');
    if (normalized === 'area') return t('roleo_auto_filter_area');
    return t('roleo_auto_filter_unset');
  };

  const fallbackCourtName = (id) => `${t('roleo_auto_modal_col_court')} ${id}`;

  useEffect(() => {
    let mounted = true;

    const fetchTribunals = async () => {
      try {
        setTribunalsLoading(true);
        const resp = await getAllCourts();
        if (!mounted) return;

        const mapped = (resp || [])
          .map((item) => {
            const regionId =
              item.region ?? item.regionId ?? item.regionValue ?? null;
            const regionAlias =
              item.regionAlias ??
              item.regionName ??
              item.nombreRegion ??
              item.regionNombre ??
              null;

            return {
              id: item.value ?? item.id ?? item.codigo ?? item.codigoTribunal,
              name: item.label ?? item.name ?? item.nombre ?? '',
              regionId,
              region: regionAlias ?? regionId,
              type: item.type ?? item.tipo ?? item.tipoTribunal ?? null,
            };
          })
          .filter((item) => item.id != null && item.name);

        setTribunals(mapped);
      } catch (error) {
        console.error('Error loading courts:', error);
      } finally {
        if (mounted) {
          setTribunalsLoading(false);
        }
      }
    };

    fetchTribunals();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchRegions = async () => {
      try {
        setRegionsLoading(true);
        const resp = await getRegiones();
        if (!mounted) return;
        setRegions(Array.isArray(resp) ? resp : []);
      } catch (error) {
        console.error('Error loading regions:', error);
      } finally {
        if (mounted) {
          setRegionsLoading(false);
        }
      }
    };

    fetchRegions();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchAreas = async () => {
      try {
        setAreasLoading(true);
        const resp = await getAllBusinessAreas();
        if (!mounted) return;

        const mapped = (resp || [])
          .map((area) => {
            const rawValue =
              area.businessAreaId ?? area.businessareaid ?? area.id ?? null;
            const value =
              rawValue === null || rawValue === undefined || rawValue === ''
                ? null
                : Number(rawValue);

            return {
              label:
                area.businessAreaName ||
                area.businessareaname ||
                area.name ||
                '',
              value,
            };
          })
          .filter((item) => item.value !== null);

        setAreaOptions([areaAllOption, ...mapped]);
      } catch (error) {
        console.error('Error loading areas:', error);
      } finally {
        if (mounted) {
          setAreasLoading(false);
        }
      }
    };

    fetchAreas();

    return () => {
      mounted = false;
    };
  }, []);

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const selectedFilter = filterValue;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [filterValue]);

  useEffect(() => {
    setPage(0);
  }, [areaValue]);

  useEffect(() => {
    let mounted = true;

    const fetchRoleo = async () => {
      try {
        setLoading(true);
        const backendPage = page + 1;
        const backendSortKey = mapSortKeyToBackend(sortKey);
        const backendSortDesc =
          sortDirection === null ? false : sortDirection === 'desc';
        const typeSearchParam =
          selectedFilter === 'all' ? null : selectedFilter;
        const selectedArea = areaValue;
        const businessAreaId =
          selectedArea === 'all' || selectedArea === null
            ? null
            : Number(selectedArea);

        const response = await getTribunalesUsuarioPaged(
          backendPage,
          pageSize,
          searchDebounced || null,
          typeSearchParam,
          businessAreaId,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mapped = (response?.data || []).map((item) => {
          const tribunalsList = normalizeTribunalItems(item.tribunalList);
          const itemBusinessAreaId =
            item.businessAreaId ?? item.businessareaid ?? null;
          const businessAreaName =
            item.businessAreaName ??
            item.businessareaname ??
            (itemBusinessAreaId
              ? businessAreaById[itemBusinessAreaId] || ''
              : '');
          return {
            id: item.id,
            user: item.userName || item.email || '-',
            email: item.email || '',
            filterType: normalizeTypeSearch(item.typeSearch),
            businessAreaId: itemBusinessAreaId,
            businessAreaName,
            tribunalIds: tribunalsList
              .map((tribunal) => tribunal.id)
              .filter((id) => id != null),
            tribunalNames: tribunalsList
              .map((tribunal) => tribunal.name)
              .filter(Boolean),
            courtCount:
              item.courtCount != null
                ? Number(item.courtCount)
                : tribunalsList.length,
          };
        });

        setRows(mapped);
        setTotal(response?.total ?? 0);
      } catch (error) {
        console.error('Error loading automatic assignment:', error);
        if (mounted) {
          showSnackbar(
            error.customMessage || t('roleo_auto_error_load'),
            'error'
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchRoleo();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    selectedFilter,
    areaValue,
    sortKey,
    sortDirection,
    reloadKey,
    businessAreaById,
    showSnackbar,
  ]);

  useEffect(() => {
    if (!actionsOpen) return;

    const handleClickOutside = (event) => {
      if (!actionsMenuRef.current) return;
      if (!actionsMenuRef.current.contains(event.target)) {
        setActionsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [actionsOpen]);

  if (
    !Card ||
    !Input ||
    !Dropdown ||
    !Table ||
    !IconButton ||
    !Badge ||
    !Button ||
    !Modal ||
    !ComboBox
  ) {
    return null;
  }

  const getCourtsDisplay = (row) => {
    if (Array.isArray(row.tribunalNames) && row.tribunalNames.length > 0) {
      return row.tribunalNames;
    }
    const ids = Array.isArray(row.tribunalIds) ? row.tribunalIds : [];
    if (!ids.length) return [];
    return ids.map((id) => tribunalsById[id] || fallbackCourtName(id));
  };

  const openModal = (row) => {
    setSelectedUser({
      ...row,
      courts: getCourtsDisplay(row),
      tribunalIds: row.tribunalIds || [],
      businessAreaId: row.businessAreaId ?? null,
    });
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    if (!payload?.userid) return;
    try {
      const updaterId = getCurrentUser()?.userId ?? null;
      await updateTribunalesUsuarios(
        payload.userid,
        payload.tipoBusqueda,
        payload.tribunales || [],
        updaterId
      );

      const nextFilterType =
        payload.tipoBusqueda === 'tribunal'
          ? 'court'
          : payload.tipoBusqueda === 'area'
            ? 'area'
            : 'case';
      const courts =
        nextFilterType === 'court' || nextFilterType === 'area'
          ? (payload.tribunales || [])
              .map((id) => tribunalsById[id] || fallbackCourtName(id))
              .filter(Boolean)
          : [];

      setRows((prev) =>
        prev.map((row) =>
          row.id === payload.userid
            ? {
                ...row,
                filterType: nextFilterType,
                businessAreaId: row.businessAreaId ?? null,
                businessAreaName: row.businessAreaName ?? '',
                tribunalIds: payload.tribunales || [],
                tribunalNames: courts,
                courts,
              }
            : row
        )
      );

      setShowModal(false);
      setSelectedUser(null);
      setReloadKey((prev) => prev + 1);
      showSnackbar(t('roleo_auto_success_save'), 'success');
    } catch (error) {
      console.error('Error saving automatic assignment:', error);
      showSnackbar(error.customMessage || t('roleo_auto_error_save'), 'error');
    }
  };

  const fetchAllRoleo = async () => {
    const pageSizeToUse = 1000;
    let backendPage = 1;
    let totalItems = 0;
    let allItems = [];

    const backendSortKey = mapSortKeyToBackend(sortKey);
    const backendSortDesc =
      sortDirection === null ? false : sortDirection === 'desc';
    const typeSearchParam = selectedFilter === 'all' ? null : selectedFilter;
    const selectedArea = areaValue;
    const businessAreaId =
      selectedArea === 'all' || selectedArea === null
        ? null
        : Number(selectedArea);

    while (true) {
      const response = await getTribunalesUsuarioPaged(
        backendPage,
        pageSizeToUse,
        searchDebounced || null,
        typeSearchParam,
        businessAreaId,
        backendSortKey,
        backendSortDesc
      );

      const items = response?.data || [];
      const totalItemsFromResponse = response?.total ?? 0;

      if (backendPage === 1) {
        totalItems = totalItemsFromResponse;
      }

      allItems = [...allItems, ...items];

      if (allItems.length >= totalItems || items.length === 0) {
        break;
      }

      backendPage += 1;
    }

    return allItems;
  };

  const buildExportRows = (items) => {
    const userHeader = t('roleo_auto_export_header_user');
    const emailHeader = t('roleo_auto_export_header_email');
    const filterHeader = t('roleo_auto_export_header_filter');
    const courtsHeader = t('roleo_auto_export_header_courts');

    return items.map((item) => {
      const tribunalsList = normalizeTribunalItems(item.tribunalList);
      const tribunalNames = tribunalsList
        .map((tribunal) => {
          if (tribunal.name) return tribunal.name;
          if (tribunal.id != null) {
            return tribunalsById[tribunal.id] || fallbackCourtName(tribunal.id);
          }
          return null;
        })
        .filter(Boolean);

      return {
        [userHeader]: item.userName || item.email || t('roleo_auto_user_fallback'),
        [emailHeader]: item.email || '',
        [filterHeader]: getFilterLabel(item.typeSearch),
        [courtsHeader]: tribunalNames.join(';'),
      };
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const typeSearchParam = selectedFilter === 'all' ? null : selectedFilter;
      const selectedArea = areaValue;
      const businessAreaId =
        selectedArea === 'all' || selectedArea === null
          ? null
          : Number(selectedArea);

      await logRoleoAutomaticoDownload({
        type: 'export',
        userId: getCurrentUser()?.userId ?? null,
        typeSearch: typeSearchParam,
        tribunalIds: [],
        search: searchDebounced || null,
        businessAreaId,
        isEnabled: true,
      });

      const allItems = await fetchAllRoleo();
      if (!allItems.length) return;

      const formatted = buildExportRows(allItems);
      const worksheet = XLSX.utils.json_to_sheet(formatted, {
        header: exportHeaders,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('roleo_auto_export_sheet'));

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(blob, t('roleo_auto_export_file'));
    } catch (error) {
      console.error('Error exporting automatic assignment:', error);
      showSnackbar(t('roleo_auto_error_export'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const typeSearchParam = selectedFilter === 'all' ? null : selectedFilter;
    const selectedArea = areaValue;
    const businessAreaId =
      selectedArea === 'all' || selectedArea === null
        ? null
        : Number(selectedArea);
    logRoleoAutomaticoDownload({
      type: 'template',
      userId: getCurrentUser()?.userId ?? null,
      typeSearch: typeSearchParam,
      tribunalIds: [],
      search: searchDebounced || null,
      businessAreaId,
      isEnabled: true,
    });

    const worksheet = XLSX.utils.aoa_to_sheet([templateHeaders, templateExample]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('roleo_auto_template_sheet'));

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });

    saveAs(blob, t('roleo_auto_template_file'));
  };

  const handleImportClick = () => {
    setActionsOpen(false);
    if (importing) return;
    importInputRef.current?.click();
  };

  const handleImportChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    try {
      setImporting(true);
      const updaterId = getCurrentUser()?.userId ?? null;
      await updateTribunalesUsuariosExcel(file, updaterId);
      setReloadKey((prev) => prev + 1);
      showSnackbar(t('roleo_auto_success_import'), 'success');
    } catch (error) {
      console.error('Error importing automatic assignment:', error);
      showSnackbar(error.customMessage || t('roleo_auto_error_import'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleExportClick = async () => {
    setActionsOpen(false);
    if (exporting) return;
    await handleExport();
  };

  const handleTemplateClick = () => {
    setActionsOpen(false);
    handleDownloadTemplate();
  };

  const selectedFilterOption =
    filterOptions.find((option) => option.value === filterValue) ||
    filterOptions[0];

  const selectedAreaOption =
    areaOptions.find((option) => option.value === areaValue) || areaAllOption;

  return (
    <div className='configuration-page roleo-automatico-page'>
      <Card specialPadding='p-4'>
        <div className='mb-3'>
          <h2 className='mb-1'>{t('roleo_auto_page_title')}</h2>
          <p className='caption font-light'>{t('roleo_auto_page_subtitle')}</p>
        </div>

        <div className='roleo-disclaimer mb-3'>
          <h4>{t('roleo_auto_disclaimer_title')}</h4>
          <ul className='caption mt-2'>
            <li>
              <strong>{t('roleo_auto_filter_case')}:</strong>{' '}
              {t('roleo_auto_disclaimer_case')}
            </li>
            <li>
              <strong>{t('roleo_auto_filter_court')}:</strong>{' '}
              {t('roleo_auto_disclaimer_court')}
            </li>
            <li>
              <strong>{t('roleo_auto_filter_area')}:</strong>{' '}
              {t('roleo_auto_disclaimer_area')}
              <Button
                label={t('roleo_auto_disclaimer_configure_areas')}
                variant='naked'
                size='sm'
                onClick={() =>
                  window.location.assign('/configuracion/gestion-datos/areas')
                }
              />
            </li>
          </ul>
        </div>

        <div className='row g-3 mb-3'>
          <div className='col-12 col-lg-6 search-align'>
            <Input
              icon='search'
              name='searchRoleo'
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('roleo_auto_search_placeholder')}
              type='text'
              value={search}
            />
          </div>
          <div className='col-12 col-lg-3'>
            <Dropdown
              label={t('roleo_auto_filter_label')}
              options={filterOptions}
              value={selectedFilterOption}
              onChange={(opt) => setFilterValue(opt?.value ?? 'all')}
              placeholder={t('roleo_auto_filter_all')}
              width='100%'
            />
          </div>
          <div className='col-12 col-lg-3'>
            <Dropdown
              options={areaOptions}
              value={selectedAreaOption}
              onChange={(opt) => setAreaValue(opt?.value ?? 'all')}
              placeholder={t('roleo_auto_filter_all')}
              width='100%'
              label={t('roleo_auto_area_label')}
              disabled={areasLoading}
            />
          </div>
        </div>

        <div className='d-flex justify-content-end mb-3'>
          <div className='roleo-actions' ref={actionsMenuRef}>
            <Button
              iconEnd='chevronDown'
              label={t('roleo_auto_actions_button')}
              variant='naked'
              size='sm'
              onClick={() => setActionsOpen((prev) => !prev)}
              className='roleo-actions__trigger'
              aria-haspopup='menu'
              aria-expanded={actionsOpen}
              aria-controls='roleo-actions-menu'
              disabled={!canExportImport}
            />
            {actionsOpen && (
              <div
                className='roleo-actions__menu'
                role='menu'
                id='roleo-actions-menu'
              >
                <button
                  type='button'
                  className='roleo-actions__item'
                  onClick={handleImportClick}
                  disabled={importing || !canExportImport}
                >
                  {t('roleo_auto_action_import')}
                </button>
                <button
                  type='button'
                  className='roleo-actions__item'
                  onClick={handleExportClick}
                  disabled={exporting || !canExportImport}
                >
                  {t('roleo_auto_action_export')}
                </button>
                <div className='roleo-actions__divider' role='separator' />
                <button
                  type='button'
                  className='roleo-actions__item'
                  onClick={handleTemplateClick}
                  disabled={!canExportImport}
                >
                  {t('roleo_auto_action_template')}
                </button>
              </div>
            )}
            <input
              ref={importInputRef}
              type='file'
              accept='.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
              onChange={handleImportChange}
              className='roleo-actions__file-input'
            />
          </div>
        </div>

        <div className='table-container roleo-automatico-table'>
          <Table
            key={`table-${tableKey}-${pageSize}`}
            columns={[
              { accessor: 'user', label: t('roleo_auto_table_user'), sort: true },
              { accessor: 'email', label: t('roleo_auto_table_email'), sort: true },
              {
                accessor: 'businessAreaName',
                label: t('roleo_auto_table_area'),
                render: (row) => {
                  const currentAreaId = row.businessAreaId;
                  if (row.businessAreaName) return row.businessAreaName;
                  return currentAreaId != null
                    ? businessAreaById[currentAreaId] || '-'
                    : '-';
                },
              },
              {
                accessor: 'filterType',
                label: t('roleo_auto_table_filter'),
                sort: true,
                render: (row) => (
                  <Badge
                    text={getFilterLabel(row.filterType)}
                    color={getFilterColor(row.filterType)}
                    size='sm'
                  />
                ),
              },
              {
                accessor: 'courts',
                label: t('roleo_auto_table_courts'),
                sort: true,
                render: (row) => {
                  const courts = getCourtsDisplay(row);
                  if (!courts.length) {
                    return <span className='color-gray-600'>-</span>;
                  }

                  const visible = courts.slice(0, 1);
                  const remaining = courts.length - visible.length;

                  return (
                    <div className='roleo-courts'>
                      {visible.map((court) => (
                        <span key={court} className='roleo-pill'>
                          {court}
                        </span>
                      ))}
                      {remaining > 0 && (
                        <span className='roleo-pill roleo-pill--muted'>
                          {t('roleo_auto_table_more', { count: remaining })}
                        </span>
                      )}
                    </div>
                  );
                },
              },
              {
                label: '',
                render: (row) => (
                  <IconButton
                    ariaLabel={t('roleo_auto_edit_aria', { user: row.user })}
                    icon='pencil'
                    onClick={() => openModal(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                    disabled={!canEditAutoRotation}
                  />
                ),
              },
            ]}
            data={rows}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
              setTableKey((prev) => prev + 1);
            }}
            paginationMode='server'
            totalItems={total}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            loading={loading}
          />
          {!loading && rows.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('roleo_auto_table_empty')}</p>
            </div>
          )}
        </div>
      </Card>
      <RoleoAutomaticoModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        Table={Table}
        Button={Button}
        IconButton={IconButton}
        ComboBox={ComboBox}
        SvgIcon={SvgIcon}
        show={showModal}
        user={selectedUser}
        areaOptions={areaOptions}
        tribunals={tribunals}
        loadingTribunals={tribunalsLoading}
        regions={regions}
        loadingRegions={regionsLoading}
        onClose={() => {
          setShowModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSave}
        onAssignArea={async (businessAreaId) => {
          const updaterId = getCurrentUser()?.userId ?? null;
          if (!selectedUser?.id) return;
          try {
            await updateUserBusinessArea({
              userId: selectedUser.id,
              businessAreaId,
              updaterId,
            });
            const areaName =
              areaOptions.find((opt) => opt.value === businessAreaId)?.label ||
              '';
            setRows((prev) =>
              prev.map((row) =>
                row.id === selectedUser.id
                  ? {
                      ...row,
                      businessAreaId,
                      businessAreaName: areaName,
                    }
                  : row
              )
            );
            setSelectedUser((prev) =>
              prev
                ? {
                    ...prev,
                    businessAreaId,
                    businessAreaName: areaName,
                  }
                : prev
            );
            showSnackbar(t('roleo_auto_success_assign_area'), 'success');
          } catch (error) {
            console.error('Error assigning area to user:', error);
            showSnackbar(
              error.customMessage || t('roleo_auto_error_assign_area'),
              'error'
            );
          }
        }}
      />
    </div>
  );
}
